import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { formatDistanceToNow } from 'date-fns';
import { apiService } from '../../lib/api';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { themeClasses } from '../../lib/theme';
import { matchesJointType } from '../../utils/jointFilters';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  type: 'post' | 'user' | 'tag';
  id: string;
  title: string;
  subtitle?: string;
  content?: string;
  avatar?: string;
  timestamp?: string;
  data?: any;
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedJointType, setSelectedJointType] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { showInfo } = useNotifications();

  const JOINT_TYPES = ['Hip', 'Knee', 'Shoulder', 'Foot & Ankle'] as const;

  // Fetch search results from backend (only when we have a search query)
  const { data: searchPosts, isLoading: isSearchLoading } = useQuery({
    queryKey: ['search', searchQuery.trim()],
    queryFn: () => apiService.searchPosts(searchQuery.trim()),
    enabled: !!searchQuery.trim(),
    staleTime: 30 * 1000, // Cache for 30 seconds
  });

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('chime_recent_searches');
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (error) {
      console.warn('Failed to load recent searches:', error);
    }
  }, []);

  // Focus search input when modal opens and prevent body scroll
  useEffect(() => {
    if (isOpen) {
      // Focus input
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }

      // Prevent body scroll
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      setSelectedIndex(-1);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Use backend search results when available
    const searchResults: SearchResult[] = [];

    if (searchPosts && !isSearchLoading) {
      let filteredPosts = searchPosts;

      // Filter by selected joint type if any
      if (selectedJointType) {
        filteredPosts = searchPosts.filter(post => matchesJointType(post, selectedJointType));
      }

      const postResults = filteredPosts
        .slice(0, 10) // Limit to 10 post results
        .map(post => ({
          type: 'post' as const,
          id: post.id.toString(),
          title: post.title,
          subtitle: post.owner.name || post.owner.username,
          content: post.description.substring(0, 100) + (post.description.length > 100 ? '...' : ''),
          avatar: post.owner.profilePicture || undefined,
          timestamp: formatDistanceToNow(new Date(post.createdAt), { addSuffix: true }),
          data: post
        }));

      searchResults.push(...postResults);
    }

    // Add mock users and tags for demo
    const mockUsers = [
      {
        type: 'user' as const,
        id: 'user1',
        title: 'Dr. Sarah Johnson',
        subtitle: 'Orthopedic Surgeon',
        avatar: 'https://i.pravatar.cc/40?u=sarah'
      },
      {
        type: 'user' as const,
        id: 'user2', 
        title: 'Dr. Michael Chen',
        subtitle: 'Sports Medicine Specialist',
        avatar: 'https://i.pravatar.cc/40?u=michael'
      }
    ];

    const mockTags = [
      {
        type: 'tag' as const,
        id: 'tag1',
        title: '#hip',
        subtitle: '245 posts'
      },
      {
        type: 'tag' as const,
        id: 'tag2',
        title: '#orthopedic',
        subtitle: '1,234 posts'
      }
    ];

    // Filter mock data
    const userResults = mockUsers.filter(user =>
      user.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const tagResults = mockTags.filter(tag =>
      tag.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    searchResults.push(...userResults, ...tagResults);

    setResults(searchResults);
    setSelectedIndex(-1);
    setIsLoading(isSearchLoading);
  }, [searchQuery, searchPosts, isSearchLoading, selectedJointType]);

  const addToRecentSearches = (query: string) => {
    if (!query.trim()) return;
    
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 10);
    setRecentSearches(updated);
    
    try {
      localStorage.setItem('chime_recent_searches', JSON.stringify(updated));
    } catch (error) {
      console.warn('Failed to save recent search:', error);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    addToRecentSearches(searchQuery);
    onClose();

    switch (result.type) {
      case 'post':
        // Navigate to search results page with hash - SearchResults component will handle scrolling
        navigate(`/search?q=${encodeURIComponent(searchQuery)}#post-${result.id}`);
        break;
      case 'user':
        // For now, navigate to search results until user profiles are implemented
        navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
        showInfo(
          `Searched for user: ${result.title}`,
          'User profiles will be available soon'
        );
        break;
      case 'tag':
        // For now, navigate to search results until tag filtering is implemented
        navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
        showInfo(
          `Showing posts with tag ${result.title}`,
          'Tag filtering will be available soon'
        );
        break;
    }
  };

  const handleRecentSearchClick = (query: string) => {
    onClose();
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem('chime_recent_searches');
    } catch (error) {
      console.warn('Failed to clear recent searches:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0) {
        // User selected a specific result with arrow keys
        handleResultClick(results[selectedIndex]);
      } else if (searchQuery.trim()) {
        // User pressed Enter without selecting - search all results
        addToRecentSearches(searchQuery);
        onClose();
        navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      }
    }
  };

  const getResultIcon = (type: SearchResult['type'], result?: SearchResult) => {
    switch (type) {
      case 'post':
        return (
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      case 'user':
        return (
          <div className="w-8 h-8 bg-healthy-green rounded-full flex items-center justify-center">
            {result?.avatar ? (
              <img src={result.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        );
      case 'tag':
        return (
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Search Modal Container */}
      <div className="fixed inset-x-0 top-0 z-[10000]">
        <div className={`${themeClasses.bgPrimary} shadow-2xl border-b ${themeClasses.borderPrimary}`}>
          <div className="max-w-3xl mx-auto">
            {/* Search Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
                <svg className={`h-6 w-6 ${themeClasses.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search posts, people, tags..."
                className={`w-full pl-16 pr-24 py-5 text-lg ${themeClasses.bgPrimary} ${themeClasses.textPrimary} border-b ${themeClasses.borderPrimary} focus:outline-none focus:border-healthy-green transition-colors`}
                autoFocus
              />
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center gap-2">
                {isLoading && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-healthy-green"></div>
                )}
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${themeClasses.textMuted} hover:text-healthy-green`}
                    title="Clear search"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={onClose}
                  className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${themeClasses.textMuted} hover:text-healthy-green`}
                  title="Close search (Esc)"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Joint Type Filter - Always visible like mobile */}
            <div className={`px-6 py-4 border-b ${themeClasses.borderPrimary}`}>
              <div className="flex gap-2 flex-wrap">
                {JOINT_TYPES.map((joint) => (
                  <button
                    key={joint}
                    onClick={() => setSelectedJointType(selectedJointType === joint ? null : joint)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedJointType === joint
                        ? 'bg-healthy-green text-white shadow-md'
                        : `${themeClasses.bgSecondary} ${themeClasses.textSecondary} hover:bg-healthy-green/10`
                    }`}
                  >
                    {joint}
                  </button>
                ))}
              </div>
            </div>

            {/* Results */}
            <div className={`max-h-[60vh] overflow-y-auto ${themeClasses.bgPrimary}`}>
            {!searchQuery.trim() ? (
              /* Recent Searches */
              recentSearches.length > 0 ? (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-white">Recent searches</h3>
                    <button
                      onClick={clearRecentSearches}
                      className="text-sm text-healthy-green hover:text-green-700"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="space-y-2">
                    {recentSearches.map((query, index) => (
                      <button
                        key={index}
                        onClick={() => handleRecentSearchClick(query)}
                        className={`flex items-center w-full px-3 py-2 text-left ${themeClasses.buttonHover} rounded-md transition-colors`}
                      >
                        <svg className={`w-4 h-4 ${themeClasses.textMuted} mr-3`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className={themeClasses.textSecondary}>{query}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null
            ) : results.length > 0 ? (
              /* Search Results */
              <div className={`divide-y ${themeClasses.borderPrimary}`}>
                {/* View all results button */}
                <button
                  onClick={() => {
                    addToRecentSearches(searchQuery);
                    onClose();
                    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                  }}
                  className={`w-full p-4 text-left hover:bg-healthy-green/10 transition-all border-b-2 ${themeClasses.borderPrimary} hover:border-healthy-green/30`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-sm font-medium ${themeClasses.textPrimary}`}>
                        View all results for "{searchQuery}"
                      </h4>
                      <p className={`text-xs ${themeClasses.textSecondary}`}>
                        See all {results.length} search results
                      </p>
                    </div>
                    <svg className={`w-4 h-4 ${themeClasses.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>

                {results.map((result, index) => (
                  <button
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className={`w-full p-4 text-left transition-all ${
                      selectedIndex === index
                        ? 'bg-healthy-green/10 border-l-4 border-l-healthy-green'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-4 border-l-transparent'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {getResultIcon(result.type, result)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className={`text-sm font-medium ${themeClasses.textPrimary} truncate`}>
                            {result.title}
                          </h4>
                          {result.timestamp && (
                            <span className={`text-xs ${themeClasses.textMuted} ml-2`}>
                              {result.timestamp}
                            </span>
                          )}
                        </div>
                        {result.subtitle && (
                          <p className={`text-sm ${themeClasses.textSecondary} truncate`}>
                            {result.subtitle}
                          </p>
                        )}
                        {result.content && (
                          <p className={`text-xs ${themeClasses.textTertiary} truncate mt-1`}>
                            {result.content}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : searchQuery.trim() && !isLoading ? (
              /* No Results */
              <div className="p-8 text-center">
                <div className={`w-16 h-16 ${themeClasses.bgSecondary} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <svg className={`w-8 h-8 ${themeClasses.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className={`text-lg font-medium ${themeClasses.textPrimary} mb-2`}>No results found</h3>
                <p className={`${themeClasses.textSecondary} text-sm`}>Try adjusting your search terms</p>
              </div>
            ) : null }
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
